import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import { CHANNEL_SUBSCRIPTION_PATHS, ensureArray, findArrayAtPaths, normalizeChannelId } from './sourcePaths';
import { coerceDate, formatNextBillingLabel, formatStatus, resolveAmountValue, resolveCancelAtDate, resolveCurrency, resolveNextBillingDate } from './valueResolvers';

export function normalizeOwnerProfileType(...values) {
  return resolveUserProfileTypeWithFallback('gallery', ...values);
}

export function normalizeChannelSubscription(record, index) {
  if (!record || typeof record !== 'object') {
    return null;
  }

  const sourceChannel = (record.channel && typeof record.channel === 'object')
    ? record.channel
    : record;
  const channelId = normalizeChannelId(
    record.channelId
    ?? record.channel_id
    ?? sourceChannel?._id
    ?? sourceChannel?.id
    ?? record.product
    ?? record.product_id
  );
  if (!channelId) {
    return null;
  }

  const title =
    sourceChannel.title
    ?? sourceChannel.name
    ?? sourceChannel.channel_name
    ?? sourceChannel.channelName
    ?? record.channel_title
    ?? `Channel ${index + 1}`;

  const owner = sourceChannel.owner && typeof sourceChannel.owner === 'object'
    ? sourceChannel.owner
    : (record.owner && typeof record.owner === 'object' ? record.owner : null);
  const ownerUsername =
    owner?.user_username
    ?? sourceChannel.owner_username
    ?? record.owner_username
    ?? record.ownerUsername
    ?? null;
  const ownerImage = resolveUserProfileImage(
    owner?.user_image,
    owner?.profile_image,
    sourceChannel.owner_image,
    sourceChannel.ownerImage,
    record.owner_image,
    record.ownerImage,
    record.owner?.user_image,
  );
  const ownerType = normalizeOwnerProfileType(
    owner?.user_type,
    owner?.type,
    owner?.__t,
    sourceChannel.owner_type,
    sourceChannel.ownerType,
    sourceChannel.owner?.user_type,
    sourceChannel.owner?.__t,
    record.owner_type,
    record.ownerType,
    record.owner?.user_type,
    record.owner?.__t
  );
  const channelKind =
    sourceChannel.channel_kind
    ?? sourceChannel.channelKind
    ?? sourceChannel.kind
    ?? record.channel_kind
    ?? record.channelKind
    ?? null;

  const channelType = String(
    sourceChannel.type
    ?? sourceChannel.channel_type
    ?? sourceChannel.channelType
    ?? ''
  ).toLowerCase();
  const amountValue =
    resolveAmountValue(record)
    ?? resolveAmountValue(sourceChannel)
    ?? Number(sourceChannel.price ?? NaN);
  const hasAmount = Number.isFinite(amountValue) && amountValue > 0;
  const isSubscribersType = channelType === 'subscribers' || channelType.includes('subscriber');
  const isPaidChannel = Boolean(record.isPaidChannel) || isSubscribersType || hasAmount;
  if (!isPaidChannel) {
    return null;
  }

  const explicitCurrency =
    sourceChannel.currency
    ?? sourceChannel.currency_code
    ?? sourceChannel.currencyCode
    ?? record.currency
    ?? record.currency_code
    ?? record.currencyCode
    ?? null;
  const currencyCode = (typeof explicitCurrency === 'string' && explicitCurrency.trim())
    ? explicitCurrency.trim()
    : resolveCurrency(record);
  const amountLabel = hasAmount
    ? formatCurrencyAmount(amountValue, currencyCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;
  const nextBillingDate = resolveNextBillingDate(record) || resolveNextBillingDate(sourceChannel);
  const periodStartDate =
    coerceDate(record.currentPeriodStart ?? record.current_period_start)
    ?? coerceDate(record.startedAt ?? record.started_at)
    ?? coerceDate(record.createdAt ?? record.created_at)
    ?? coerceDate(sourceChannel.createdAt ?? sourceChannel.created_at)
    ?? null;
  const periodEndDate =
    resolveCancelAtDate(record)
    ?? coerceDate(record.expiresAt ?? record.expires_at)
    ?? nextBillingDate
    ?? null;
  const recurringCandidate =
    (typeof record.isRecurring === 'boolean' ? record.isRecurring : undefined)
    ?? (typeof record.is_recurring === 'boolean' ? record.is_recurring : undefined)
    ?? (typeof record.recurring === 'boolean' ? record.recurring : undefined)
    ?? (typeof sourceChannel.isRecurring === 'boolean' ? sourceChannel.isRecurring : undefined)
    ?? (typeof sourceChannel.is_recurring === 'boolean' ? sourceChannel.is_recurring : undefined)
    ?? (typeof sourceChannel.recurring === 'boolean' ? sourceChannel.recurring : undefined);
  const isRecurring = recurringCandidate ?? true;

  const rawStatus = record.status ?? record.state ?? sourceChannel.status ?? sourceChannel.state ?? null;
  const normalizedStatus = typeof rawStatus === 'string'
    ? rawStatus.toLowerCase().trim()
    : '';
  const hasActiveStatus = [
    'active',
    'trialing',
    'paid',
    'confirmed',
    'active_subscription',
  ].includes(normalizedStatus);
  const hasCancelledStatus = [
    'cancelled',
    'canceled',
    'inactive',
    'ended',
    'expired',
    'past_due',
    'unpaid',
  ].includes(normalizedStatus);
  const cancelAtPeriodEnd =
    record.cancel_at_period_end
    ?? record.cancelAtPeriodEnd
    ?? record.cancelled_at_period_end
    ?? record.cancelledAtPeriodEnd
    ?? sourceChannel.cancel_at_period_end
    ?? sourceChannel.cancelAtPeriodEnd
    ?? false;
  const isCancelled = Boolean(
    hasCancelledStatus
    || recurringCandidate === false
    || (cancelAtPeriodEnd && !hasActiveStatus)
  );
  const explicitIsActive =
    (typeof record.isActive === 'boolean' ? record.isActive : undefined)
    ?? (typeof record.is_active === 'boolean' ? record.is_active : undefined)
    ?? (typeof sourceChannel.isActive === 'boolean' ? sourceChannel.isActive : undefined)
    ?? (typeof sourceChannel.is_active === 'boolean' ? sourceChannel.is_active : undefined);
  const isActive = explicitIsActive ?? (hasActiveStatus ? true : !isCancelled);

  return {
    id: record.id ?? record._id ?? `channel-sub-${channelId}`,
    channelId,
    title,
    ownerUsername,
    ownerImage,
    ownerType,
    channelKind,
    amountValue: hasAmount ? amountValue : null,
    amountLabel,
    currencyCode,
    nextBillingDate,
    nextBillingLabel: formatNextBillingLabel(periodEndDate ?? nextBillingDate),
    statusLabel: isCancelled ? 'Cancelled' : formatStatus(record, isActive),
    isActive,
    isRecurring,
    isCancelled,
    billing_mode:
      record?.billing_mode
      ?? record?.metadata?.billing_mode
      ?? sourceChannel?.billing_mode
      ?? sourceChannel?.metadata?.billing_mode
      ?? null,
    billing_is_business:
      record?.billing_is_business
      ?? record?.metadata?.billing_is_business
      ?? sourceChannel?.billing_is_business
      ?? sourceChannel?.metadata?.billing_is_business
      ?? null,
    periodStartDate,
    periodEndDate,
    joinedAt:
      coerceDate(record.createdAt ?? record.created_at)
      ?? coerceDate(record.joinedAt ?? record.joined_at)
      ?? coerceDate(sourceChannel.createdAt ?? sourceChannel.created_at)
      ?? null,
  };
}

export function extractChannelSubscriptions(source) {
  if (!source) {
    return [];
  }
  const raw = findArrayAtPaths(source, CHANNEL_SUBSCRIPTION_PATHS);
  if (raw.length === 0) {
    return [];
  }
  return raw
    .map(normalizeChannelSubscription)
    .filter(Boolean);
}

export function extractChannelSubscriptionsFromMembershipSummary(membershipSummary) {
  const raw = ensureArray(membershipSummary?.data ?? membershipSummary);
  if (raw.length === 0) {
    return [];
  }
  return raw
    .map(normalizeChannelSubscription)
    .filter(Boolean);
}
